/** Order endpoints (`/orders`). Clients place; staff drive the fulfilment workflow. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getData, getList, patchData, postData } from "../lib/apiClient";
import { queryKeys } from "./queryKeys";
import { Order, OrderStatus } from "../types/models";

export interface ListOrdersParams {
  page?: number;
  limit?: number;
  status?: OrderStatus;
  clientId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface PlaceOrderInput {
  items: Array<{ productId: string; quantity: number }>;
  notes?: string;
  /** Management/admin only: place the order on behalf of this client. */
  clientId?: string;
}

export const ordersApi = {
  list: (params?: ListOrdersParams) => getList<Order>("/orders", params),
  getById: (id: string) => getData<Order>(`/orders/${id}`),
  place: (input: PlaceOrderInput) => postData<Order>("/orders", input),
  updateStatus: (id: string, status: OrderStatus) =>
    patchData<Order>(`/orders/${id}/status`, { status }),
  cancel: (id: string) => postData<Order>(`/orders/${id}/cancel`),
};

// ── Hooks ────────────────────────────────────────────────────────────

export const useOrders = (params?: ListOrdersParams) =>
  useQuery({
    queryKey: queryKeys.orders.list(params),
    queryFn: () => ordersApi.list(params),
    placeholderData: (prev) => prev,
  });

export const useOrder = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.orders.detail(id ?? ""),
    queryFn: () => ordersApi.getById(id as string),
    enabled: Boolean(id),
  });

const invalidateOrders = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: queryKeys.orders.all });
  qc.invalidateQueries({ queryKey: queryKeys.dashboard.overview });
  qc.invalidateQueries({ queryKey: queryKeys.payments.all });
};

export const usePlaceOrder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PlaceOrderInput) => ordersApi.place(input),
    onSuccess: () => invalidateOrders(qc),
  });
};

export const useUpdateOrderStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderStatus }) =>
      ordersApi.updateStatus(id, status),
    onSuccess: (_d, { id }) => {
      invalidateOrders(qc);
      qc.invalidateQueries({ queryKey: queryKeys.orders.detail(id) });
    },
  });
};

export const useCancelOrder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ordersApi.cancel(id),
    onSuccess: () => invalidateOrders(qc),
  });
};
